import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {ComponentProps} from "props";
import {Affix, Box, Button, Loader, LoadingOverlay, Text, Tooltip} from "@mantine/core";
import classes from "./ProtocolDefinitionEditor.module.css"
import commonClasses from "../../common/common.module.css"
import {useProtocolDefinitionsStore} from "../store/protocolDefinitions";
import {StepListView} from "./steps/StepListView";
import {modals} from "@mantine/modals"
import {
    TProtocolDefinitionId,
    TProtocolStepDefinitionId,
    IProtocolStepDefinition,
    IGroupStepDefinition,
    createProtocolStepDefinition,
} from "../types/protocolDefinitions";
import cx from "clsx"
import {StepContentView} from "./steps/StepContentView";
import {ProtocolContentView} from "../ProtocolContentView";
import {showAddStepModal} from "../modals";
import {useNeurotuneStore} from "@/common/store/neurotune.ts";
import * as protocolsService from "../services/protocols";
import {debounce} from "lodash"
import {IProtocolStepKind} from "../types/protocol";
import {useNavigate, useParams} from "react-router";
import {IconChevronLeft} from "@tabler/icons-react";
import {showErrorNotification} from "@/common/notifications.tsx";
import * as protocolServices from "@/protocols/services/protocols.ts";

export interface ProtocolDefinitionEditorProps extends ComponentProps {
}


export const ProtocolDefinitionEditor: React.FC<ProtocolDefinitionEditorProps> = ({}: ProtocolDefinitionEditorProps) => {
    const navigate = useNavigate()
    const selectedProtocolId = useParams().pdef_id as TProtocolDefinitionId
    const activeCompany = useNeurotuneStore((state) => state.activeCompany)
    const insertStep = useProtocolDefinitionsStore((state) => state.insertStepDefinition)
    const deleteStep = useProtocolDefinitionsStore((state) => state.deleteStepDefinition)
    const updateStep = useProtocolDefinitionsStore((state) => state.updateStepDefinition)
    const updateProtocol = useProtocolDefinitionsStore((state) => state.updateProtocolDefinition)
    const loadProtocolDefinition = useProtocolDefinitionsStore((state) => state.loadProtocolDefinition)
    const protocolDefinitionsLoading = useProtocolDefinitionsStore((state) => state.protocolDefinitionsLoading)
    const protocolDefinitions = useProtocolDefinitionsStore((state) => state.protocolDefinitions)
    const [selectedStepId, setSelectedStepId] = useState<TProtocolStepDefinitionId>()
    const [saving, setSaving] = useState(false)
    const updateStepTimeoutRef = useRef<Map<TProtocolStepDefinitionId, ReturnType<typeof debounce>>>(new Map());
    const updateProtocolTimeoutRef = useRef<Map<TProtocolDefinitionId, ReturnType<typeof debounce>>>(new Map());

    const selectedProtocolDefinition = protocolDefinitions?.find((pd) => pd.id === selectedProtocolId)

    useEffect(() => {
        loadProtocolDefinition(activeCompany.id, selectedProtocolId, true).catch((_) => {
            // nothing to do here as error has  already been shown
        })
    }, [])

    const renderLoadingSavingStatus = () => {
        return (
            <Affix position={{bottom: 20, right: 20}}>
                {protocolDefinitionsLoading || saving ?
                    <Loader color="var(--bios-strong-accent)"/> : <></>}
            </Affix>
        )
    }

    /**
     * Creates and returns a debounced function for updating a protocol. This function ensures that
     * updates to protocol definitions are delayed and executed with a debounce mechanism, reducing
     * the frequency of server calls. It also manages error handling and saving states appropriately.
     *
     * If a debounced function for the given protocol definition ID does not already exist, one is
     * created and stored for reuse. Otherwise, the existing debounced function is returned.
     *
     * @param {TProtocolDefinitionId} pdefId - The unique identifier of the protocol definition to be updated.
     * @returns {Function} A debounced function for updating the protocol definition corresponding
     * to the provided protocol definition ID.
     */
    const getDebouncedUpdateProtocol = useCallback((pdefId: TProtocolDefinitionId) => {
        if (!updateProtocolTimeoutRef.current.has(pdefId)) {
            const debouncedFn = debounce(async () => {
                const latestProtocol = useProtocolDefinitionsStore.getState().protocolDefinitions.find(p => p.id === pdefId)

                if (!latestProtocol) {
                    showErrorNotification({
                        title: `Failed to save protocol ${pdefId}`,
                        message: 'The protocol could not be found.'
                    })
                    return
                }

                setSaving(true);
                try {
                    await protocolServices.updateProtocolDefinition(activeCompany.id, latestProtocol);
                } catch (error) {
                    console.error(`Failed to save protocol to server: ${pdefId}`)
                } finally {
                    setSaving(false);
                }
            }, 500, {leading: false, trailing: true});

            updateProtocolTimeoutRef.current.set(pdefId, debouncedFn);
        }
        return updateProtocolTimeoutRef.current.get(pdefId)!;
    }, [selectedProtocolId, activeCompany.id]);
    // === Flush all pending protocol definition saves on unmount ===
    useEffect(() => {
        return () => {
            // Flush = execute immediately any pending debounced calls
            updateProtocolTimeoutRef.current.forEach((debouncedFn) => {
                debouncedFn.flush();
            });

            updateProtocolTimeoutRef.current.clear();
        };
    }, []);
    /**
     * Creates and returns a debounced function for updating a protocol step definition.
     *
     * This function ensures that updates to a specific step definition are debounced,
     * reducing redundant API calls when changes happen in quick succession. The debounced
     * function is cached, ensuring the same debounced instance is reused for a given stepId
     * across calls.
     *
     * The update operation involves locating the latest step definition, setting a "saving"
     * state, and calling an asynchronous API to persist the changes. If the step cannot be
     * found or an error occurs while saving, appropriate error handling is performed, including
     * logging and displaying error notifications.
     *
     * @param {TProtocolStepDefinitionId} stepId - The unique identifier for the protocol step
     * definition to be updated.
     * @returns {Function} A debounced function that performs the protocol step update operation
     * when invoked.
     */
    const getDebouncedUpdateStep = useCallback((stepId: TProtocolStepDefinitionId) => {
        if (!updateStepTimeoutRef.current.has(stepId)) {
            const debouncedFn = debounce(async () => {
                const latestStep = useProtocolDefinitionsStore.getState().protocolDefinitions.find(p => p.id === selectedProtocolId)?.steps
                    .find(s => s.id === stepId);

                if (!latestStep) {
                    showErrorNotification({
                        title: `Failed to save step ${stepId}`,
                        message: 'The step could not be found.'
                    })
                    return
                }

                setSaving(true);
                try {
                    await protocolServices.updateProtocolStepDefinition(activeCompany.id, latestStep);
                } catch (error) {
                    console.error(`Failed to save step ${stepId}`, error);
                } finally {
                    setSaving(false);
                }
            }, 500, {leading: false, trailing: true});

            updateStepTimeoutRef.current.set(stepId, debouncedFn);
        }
        return updateStepTimeoutRef.current.get(stepId)!;
    }, [selectedProtocolId, activeCompany.id]);
    // === Flush all pending saves on unmount ===
    useEffect(() => {
        return () => {
            // Flush = execute immediately any pending debounced calls
            updateStepTimeoutRef.current.forEach((debouncedFn) => {
                debouncedFn.flush();
            });

            updateStepTimeoutRef.current.clear();
        };
    }, []);


    const selectedStep = useMemo(() => {
        const selectedStep = selectedProtocolDefinition?.steps?.find((st => st.id === selectedStepId))
        if (selectedStepId && !selectedStep) {
            setSelectedStepId(undefined)
        }
        return selectedStep
    }, [selectedProtocolDefinition, selectedStepId])

    // Get all step IDs that are members of any group
    const groupedStepIds = useMemo(() => {
        if (!selectedProtocolDefinition?.steps) return new Set<TProtocolStepDefinitionId>();
        const ids = new Set<TProtocolStepDefinitionId>();
        selectedProtocolDefinition.steps.forEach(step => {
            if (step.kind === IProtocolStepKind.GROUP) {
                const group = step as IGroupStepDefinition;
                group.memberStepIds?.forEach(id => ids.add(id));
            }
        });
        return ids;
    }, [selectedProtocolDefinition?.steps]);

    // Filter steps for left panel: only show ungrouped steps and groups
    const leftPanelSteps = useMemo(() => {
        if (!selectedProtocolDefinition?.steps) return [];
        return selectedProtocolDefinition.steps.filter(step => !groupedStepIds.has(step.id));
    }, [selectedProtocolDefinition?.steps, groupedStepIds]);


    const toggleSelectedStep = (step: IProtocolStepDefinition) => {
        if (selectedStepId === step?.id) {
            setSelectedStepId(undefined)
        } else {
            setSelectedStepId(step.id)
        }
    }
    const onDeleteStep = (protocolId: TProtocolDefinitionId, step: IProtocolStepDefinition) => {
        const isGroup = step.kind === IProtocolStepKind.GROUP;
        const groupStep = isGroup ? step as IGroupStepDefinition : null;
        const memberCount = groupStep?.memberStepIds?.length || 0;

        const confirmMessage = isGroup && memberCount > 0
            ? <Text>Delete <Text span fw={"bold"}>{step?.name}</Text> and its {memberCount} member step(s)?</Text>
            : <Text>Delete <Text span fw={"bold"}>{step?.name}?</Text></Text>;

        modals.openConfirmModal({
            className: cx(),
            size: "sm",
            zIndex: 1000,
            withCloseButton: false,
            children: confirmMessage,
            labels: {confirm: "Confirm", cancel: "Cancel"},
            onConfirm: () => {
                // If it's a group, also delete member steps from local state
                // Backend handles cascade deletion atomically
                if (isGroup && groupStep?.memberStepIds) {
                    groupStep.memberStepIds.forEach(memberId => {
                        const memberStep = selectedProtocolDefinition?.steps.find(s => s.id === memberId);
                        if (memberStep) {
                            deleteStep(activeCompany.id, protocolId, memberStep);
                        }
                    });
                }
                deleteStep(activeCompany.id, protocolId, step)
                setSelectedStepId(undefined)
                setSaving(true)
                protocolsService.deleteProtocolStepDefinition(activeCompany.id, step).finally(() => setSaving(false))
            },
        })
    }
    const onAddStep = (kind: IProtocolStepKind, name: string) => {
        if (selectedProtocolDefinition && selectedProtocolId) {
            let index = selectedProtocolDefinition.steps.findIndex((st) => st.id === selectedStep?.id)
            index = index < 0 ? 0 : (index + 1)

            setSaving(true)
            const newStep = createProtocolStepDefinition({
                kind,
                name: `${name} ${index}`,
                protocolId: selectedProtocolId,
            }, kind === IProtocolStepKind.GROUP ? {memberStepIds: []} : undefined)
            protocolsService.createProtocolStepDefinition(activeCompany.id, newStep, index).then((newStep) => {
                insertStep(activeCompany.id, selectedProtocolId, newStep, index)
                setSelectedStepId(newStep.id)
            }).finally(() => setSaving(false))
        } else {
            console.error("Adding a step to an unselected protocol???")
        }
    }

    // Add a step to a group (called from GroupStepListView)
    const onAddStepToGroup = (kind: IProtocolStepKind, name: string) => {
        if (selectedProtocolDefinition && selectedProtocolId && selectedStep?.kind === IProtocolStepKind.GROUP) {
            const group = selectedStep as IGroupStepDefinition;
            const index = selectedProtocolDefinition.steps.length; // Add at end of all steps

            setSaving(true)
            const newStep = createProtocolStepDefinition({
                kind,
                name: `${name} ${group.memberStepIds?.length || 0}`,
                protocolId: selectedProtocolId
            })
            protocolsService.createProtocolStepDefinition(activeCompany.id, newStep, index).then((createdStep) => {
                // Insert the step into the protocol
                insertStep(activeCompany.id, selectedProtocolId, createdStep, index)
                // Update the group to include the new step
                const updatedMemberIds = [...(group.memberStepIds || []), createdStep.id];
                updateStep(activeCompany.id, selectedProtocolId, group.id, {memberStepIds: updatedMemberIds});
                getDebouncedUpdateStep(group.id)()
            }).finally(() => setSaving(false))
        }
    }

    // Delete a step from a group
    const onDeleteStepFromGroup = (step: IProtocolStepDefinition) => {
        if (selectedProtocolDefinition && selectedProtocolId && selectedStep?.kind === IProtocolStepKind.GROUP) {
            const group = selectedStep as IGroupStepDefinition;

            modals.openConfirmModal({
                className: cx(),
                size: "sm",
                zIndex: 1000,
                withCloseButton: false,
                children: <Text>Delete <Text span fw={"bold"}>{step?.name}</Text> from group?</Text>,
                labels: {confirm: "Confirm", cancel: "Cancel"},
                onConfirm: () => {
                    setSaving(true);
                    // Remove from group's memberStepIds
                    const updatedMemberIds = group.memberStepIds.filter(id => id !== step.id);
                    updateStep(activeCompany.id, selectedProtocolId, group.id, {memberStepIds: updatedMemberIds});
                    getDebouncedUpdateStep(group.id)()

                    // Delete the step itself
                    deleteStep(activeCompany.id, selectedProtocolId, step);
                    protocolsService.deleteProtocolStepDefinition(activeCompany.id, step).finally(() => setSaving(false));
                },
            });
        }
    }

    // Handle reordering of steps within a group
    const onGroupMemberOrderChanged = (group: IGroupStepDefinition, newMemberStepIds: Array<TProtocolStepDefinitionId>) => {
        if (selectedProtocolId) {
            updateStep(activeCompany.id, selectedProtocolId, group.id, {memberStepIds: newMemberStepIds});
            getDebouncedUpdateStep(group.id)()
        }
    }
    const onStepsOrderChanged = (steps: Array<IProtocolStepDefinition>) => {
        if (selectedProtocolDefinition) {
            // The steps array contains only ungrouped steps (left panel)
            // We need to merge with grouped steps to get the complete list
            const groupedSteps = selectedProtocolDefinition.steps.filter(step => groupedStepIds.has(step.id));
            const allSteps = [...steps, ...groupedSteps];

            if (allSteps.length === selectedProtocolDefinition.steps.length) {
                const pdef = {...selectedProtocolDefinition, steps: allSteps}
                updateProtocol(activeCompany.id, {...selectedProtocolDefinition, steps: allSteps})
                setSelectedStepId(undefined)
                getDebouncedUpdateProtocol(pdef.id)()
            } else {
                console.error(`Selected protocol steps:${selectedProtocolDefinition.steps.length} doesn't match those received: ${allSteps.length}`)
            }
        }
    }

    const renderProtocolDefinition = () => {
        if (selectedProtocolDefinition === undefined)
            return <></>

        return (
            <div className={cx(classes.row)}>
                <Box className={cx(classes.rowHeader, commonClasses.strongBlur)}>
                    <div className={cx(classes.protocol)}
                         data-testid={`toggle-protocol-${selectedProtocolDefinition.id}`}>
                        <Button size={"compact-xs"} onClick={() => navigate(-1)}>
                            <IconChevronLeft size={"17px"}/>Back
                        </Button>
                    </div>
                    <Box className={cx(classes.toolbar)}>
                        <Button size={"compact-xs"}
                                data-testid={`add-step-protocol-${selectedProtocolDefinition.id}`}
                                onClick={
                                    () => showAddStepModal(onAddStep)
                                }>Add Step</Button>
                        <Tooltip label={selectedProtocolDefinition.name}>
                            <Text className={classes.protocolName}>{selectedProtocolDefinition.name}</Text>
                        </Tooltip>
                        <Button
                            data-testid={`delete-step-${selectedProtocolDefinition.id}`}
                            disabled={!selectedStep}
                            size={"compact-xs"}
                            onClick={() => selectedProtocolId && selectedStep && onDeleteStep(selectedProtocolId, selectedStep)}>Delete
                            Step</Button>
                    </Box>
                </Box>
                <StepListView steps={leftPanelSteps} onSelection={toggleSelectedStep}
                              selectedStep={selectedStep}
                              onStepsOrderChanged={onStepsOrderChanged}
                />
            </div>
        )
    }

    const renderSelection = () => {
        if (selectedProtocolId && selectedStep) {
            return (
                <StepContentView
                    protocolId={selectedProtocolId}
                    step={selectedStep}
                    allSteps={selectedProtocolDefinition?.steps}
                    onChange={(stepId, stepUpdates) => {
                        // Update store with stepId and partial updates
                        // This also clears device-specific fields when device kind changes
                        updateStep(activeCompany.id, selectedProtocolId, stepId, stepUpdates)
                        getDebouncedUpdateStep(stepId)()
                    }}
                    onAddStepToGroup={onAddStepToGroup}
                    onGroupMemberOrderChanged={onGroupMemberOrderChanged}
                    onDeleteStepFromGroup={onDeleteStepFromGroup}
                />
            )
        } else if (selectedProtocolDefinition) {
            return (
                <ProtocolContentView protocol={selectedProtocolDefinition} onChange={(protocol) => {
                    updateProtocol(activeCompany.id, protocol)
                    getDebouncedUpdateProtocol(protocol.id)()
                }}/>
            )
        } else {
            return ""
        }
    }

    if (protocolDefinitionsLoading)
        return <LoadingOverlay visible={true} zIndex={1000}/>

    return (
        <div className={classes.root}>
            <div className={cx(classes.sidePanel)}>
                <div className={classes.sidePanelContent}>
                    {renderProtocolDefinition()}
                </div>
            </div>
            <div className={cx(classes.content)}>
                {renderSelection()}
            </div>
            {renderLoadingSavingStatus()}
        </div>
    );
};